// frontend/src/pages/Dashboard.jsx
// WHY CHANGED:
//   - Added Section 2: Insurance Dashboard, fetched separately from
//     dashboardAPI.getInsuranceStats() (popular_wheelers_crm only).
//   - Workshop section (state, fetchStats, JSX) is 100% unchanged.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Ticket, CheckCircle2, Clock, TrendingUp,
  Search, RefreshCw, Car, CalendarDays,
  XCircle, MinusCircle, MapPin, Shield, FileText,
  AlertTriangle,
} from 'lucide-react';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : (value ?? '—')}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
      {sub && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const Dashboard = () => {
  // ── Workshop state (unchanged) ──────────────────────────────────────────
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const { branch } = useAuth();
  const navigate   = useNavigate();

  // ── NEW: Insurance state (separate, additive) ───────────────────────────
  const [insuranceStats,   setInsuranceStats]   = useState(null);
  const [insuranceLoading, setInsuranceLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await dashboardAPI.getStats();
      setStats(res.data.data);
    } catch {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  // NEW: separate fetch, separate error handling — a failure here must
  // never affect the Workshop section above.
  const fetchInsuranceStats = async () => {
    setInsuranceLoading(true);
    try {
      const res = await dashboardAPI.getInsuranceStats();
      setInsuranceStats(res.data.data);
    } catch {
      toast.error('Failed to load insurance dashboard stats');
    } finally {
      setInsuranceLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchInsuranceStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const claimPct = stats?.totalPossible
    ? Math.round((stats.totalClaimed / stats.totalPossible) * 100)
    : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header (unchanged) */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {branch && <span className="font-medium text-brand-600 dark:text-brand-400">{branch} Branch · </span>}
            {format(new Date(), 'EEEE, d MMMM yyyy')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => { fetchStats(); fetchInsuranceStats(); }}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/search')}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
          >
            <Search className="w-4 h-4" />
            Search Customer
          </button>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 1 — WORKSHOP DASHBOARD (unchanged from your original file)
         ══════════════════════════════════════════════════════════════════ */}

      {/* Main stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard icon={Users}        label="Total Customers"   value={stats?.totalCustomers ?? 0} color="bg-blue-500" />
        <StatCard icon={Ticket}       label="Total Coupons"     value={stats?.totalCoupons ?? 0}   color="bg-violet-500" />
        <StatCard
          icon={CheckCircle2}
          label="Services Claimed"
          value={stats?.totalClaimed ?? 0}
          sub={`${claimPct}% of total`}
          color="bg-emerald-500"
        />
        <StatCard icon={Clock} label="Services Remaining" value={stats?.totalRemaining ?? 0} color="bg-amber-500" />
      </div>

      {/* Coupon status row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={CheckCircle2} label="Active Coupons"    value={stats?.totalActive ?? 0}    color="bg-emerald-500" />
        <StatCard icon={XCircle}      label="Cancelled Coupons" value={stats?.totalCancelled ?? 0} color="bg-red-500" />
        <StatCard icon={MinusCircle}  label="Inactive Coupons"  value={stats?.totalInactive ?? 0}  color="bg-amber-500" />
      </div>

      {/* Progress bar */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <TrendingUp className="w-4 h-4 text-brand-500" />
            Overall Service Utilization
          </div>
          <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{claimPct}%</span>
        </div>
        <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-brand-600 rounded-full transition-all duration-700"
            style={{ width: `${claimPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-2">
          <span>{stats?.totalClaimed?.toLocaleString()} claimed</span>
          <span>{stats?.totalPossible?.toLocaleString()} total</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Coupon type breakdown */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Car className="w-4 h-4 text-brand-500" />
            Coupon Type Breakdown
          </h2>
          {stats?.couponBreakdown?.length > 0 ? (
            <div className="space-y-3">
              {stats.couponBreakdown.map((item, i) => {
                const colors = ['bg-brand-500', 'bg-violet-500', 'bg-emerald-500'];
                const pct    = Math.round((item.count / stats.totalCustomers) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">{item.coupon_type || 'Unknown'}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{parseInt(item.count).toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No data available</p>
          )}
        </div>

        {/* Recent activity */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-brand-500" />
            Recent Claimed Services
          </h2>
          {stats?.recentActivity?.length > 0 ? (
            <div className="space-y-2.5 max-h-64 overflow-y-auto">
              {stats.recentActivity.map((item, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.customer_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.service_name}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {item.service_claim_date ? format(new Date(item.service_claim_date), 'dd MMM yyyy, hh:mm a') : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No recent activity</p>
          )}
        </div>
      </div>

      {/* Branch stats */}
      {stats?.branchStats?.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-brand-500" />
            Branch-wise Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="pb-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400">Branch</th>
                  <th className="pb-2 text-right text-xs font-semibold text-gray-500 dark:text-gray-400">Total</th>
                  <th className="pb-2 text-right text-xs font-semibold text-emerald-600 dark:text-emerald-400">Active</th>
                  <th className="pb-2 text-right text-xs font-semibold text-red-600 dark:text-red-400">Cancelled</th>
                  <th className="pb-2 text-right text-xs font-semibold text-amber-600 dark:text-amber-400">Inactive</th>
                </tr>
              </thead>
              <tbody>
                {stats.branchStats.map((row, i) => (
                  <tr key={i} className="border-b border-gray-50 dark:border-gray-800/50 last:border-0">
                    <td className="py-2 font-medium text-gray-800 dark:text-gray-200">{row.branch}</td>
                    <td className="py-2 text-right text-gray-600 dark:text-gray-400">{parseInt(row.total).toLocaleString()}</td>
                    <td className="py-2 text-right text-emerald-700 dark:text-emerald-400">{parseInt(row.active).toLocaleString()}</td>
                    <td className="py-2 text-right text-red-700 dark:text-red-400">{parseInt(row.cancelled).toLocaleString()}</td>
                    <td className="py-2 text-right text-amber-700 dark:text-amber-400">{parseInt(row.inactive).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════
          SECTION 2 — INSURANCE DASHBOARD (NEW, popular_wheelers_crm only)
         ══════════════════════════════════════════════════════════════════ */}

      <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-brand-500" />
          Insurance Dashboard
        </h2>

        {insuranceLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Main insurance stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard icon={FileText} label="Total Policies" value={insuranceStats?.totalPolicies ?? 0} color="bg-indigo-500" />
              <StatCard icon={Users}    label="Total Insurance Customers" value={insuranceStats?.totalInsuranceCustomers ?? 0} color="bg-blue-500" />
              <StatCard
                icon={CheckCircle2}
                label="Total Insurance Claims"
                value={insuranceStats?.totalInsuranceClaims ?? 0}
                sub={`${insuranceStats?.utilizationPct ?? 0}% of total`}
                color="bg-emerald-500"
              />
              <StatCard icon={Clock} label="Remaining Insurance Services" value={insuranceStats?.remainingServices ?? 0} color="bg-amber-500" />
            </div>

            {/* Policy type breakdown row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard icon={Shield} label="M-30 Policies" value={insuranceStats?.m30Policies ?? 0} color="bg-sky-500" />
              <StatCard icon={Shield} label="M-45 Policies" value={insuranceStats?.m45Policies ?? 0} color="bg-violet-500" />
              <StatCard icon={Shield} label="M-60 Policies" value={insuranceStats?.m60Policies ?? 0} color="bg-fuchsia-500" />
              <StatCard icon={Shield} label="Non Tie-Up Policies" value={insuranceStats?.nonTieUpPolicies ?? 0} color="bg-gray-500" />
            </div>

            {/* Today's claims + progress */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard icon={CalendarDays} label="Today's Insurance Claims" value={insuranceStats?.todaysInsuranceClaims ?? 0} color="bg-emerald-500" />
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    <TrendingUp className="w-4 h-4 text-brand-500" />
                    Insurance Service Utilization
                  </div>
                  <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{insuranceStats?.utilizationPct ?? 0}%</span>
                </div>
                <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-brand-500 to-brand-600 rounded-full transition-all duration-700"
                    style={{ width: `${insuranceStats?.utilizationPct ?? 0}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Policy expiry summary */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-brand-500" />
                  Policy Expiry Summary
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Expired</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      {(insuranceStats?.policyExpirySummary?.expired ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Expiring in 30 Days</span>
                    <span className="font-medium text-amber-600 dark:text-amber-400">
                      {(insuranceStats?.policyExpirySummary?.expiringIn30Days ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Active</span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {(insuranceStats?.policyExpirySummary?.active ?? 0).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recent insurance claims */}
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-brand-500" />
                  Recent Insurance Claims
                </h3>
                {insuranceStats?.recentInsuranceClaims?.length > 0 ? (
                  <div className="space-y-2.5 max-h-64 overflow-y-auto">
                    {insuranceStats.recentInsuranceClaims.map((item, i) => (
                      <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                        <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.customer_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.service_name}</div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            {item.claim_date ? format(new Date(item.claim_date), 'dd MMM yyyy, hh:mm a') : '—'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 dark:text-gray-500">No recent insurance claims</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;